/**
 * Cart opt-in blocks
 * ---------------------------------------------------------------------------------------------------------------
 * Two blocks share one behaviour — a checkbox that expands a field, a debounced write, and a flush on every way
 * out of the page:
 *
 *   <cart-gift-note>   snippets/cart-gift-note.liquid   adds the gift product, writes the card message
 *   <cart-order-note>  snippets/cart-order-note.liquid  writes cart.note
 *
 * CartOptin below holds everything they have in common; each subclass supplies only its own persistence.
 *
 * Design constraints:
 *   - Ticking and opening are separate states. The checkbox is the opt-in; the row beside it only shows and hides
 *     the panel, so a field can be put away without losing what is in it. Only one panel is open at a time, and
 *     moving focus outside closes it. Without JavaScript the panel simply follows :checked.
 *   - The cart drawer replaces its entire content on every "cart:change", and the cart page reloads outright. Both
 *     destroy these elements while the customer may still be typing, so pending writes are flushed on disconnect
 *     and on pagehide, and no state is kept here that is not seeded server-side.
 *   - Writes that do not change cart totals request no sections and dispatch no event — a re-render would destroy
 *     the textarea the customer is typing in. Only adding or removing the gift line goes the full re-render route.
 */
(function () {
  if (window.customElements.get('cart-gift-note')) {
    return; // Both sections emit the script tag, so on the cart page this runs twice
  }

  const DEBOUNCE_DELAY = 500;

  // Chrome sizes a textarea to its content natively; everywhere else the height is set from scrollHeight.
  const SUPPORTS_FIELD_SIZING =
    typeof CSS !== 'undefined' && CSS.supports && CSS.supports('field-sizing', 'content');
  const SYNC_EVENT = 'cart-gift-note:change';

  // Superseded names. Written as empty strings once, which deletes them from the cart.
  const LEGACY_ATTRIBUTES = ['present-shipping', 'present-note', 'Send as a gift', 'Gift card note', 'is_gift', 'gift_message'];

  function cartUrl(endpoint) {
    return `${window.Shopify.routes.root}cart/${endpoint}.js`;
  }

  /**
   * Body-only write: no sections, no event. Used for everything that leaves the totals alone.
   */
  function writeCart(body) {
    return fetch(cartUrl('update'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true // Makes sure the request is fired even when submitting the form or unloading
    });
  }

  /**
   * Line-changing write, mirroring the theme's changeLineItemQuantity: collect the sections every cart surface
   * wants re-rendered, send them along, then hand the response back through "cart:change" so they all refresh.
   */
  async function mutateCartLine(endpoint, body) {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    let response;
    try {
      response = await fetch(cartUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...body, sections: sections.join(',') }),
        keepalive: true
      });
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }

    if (!response.ok) {
      return null;
    }

    const cart = await response.json();

    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return cart;
    }

    // Not "variant:add" on purpose: that value makes the drawer force itself open.
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'gift-note:change', cart } })
    );

    return cart;
  }

  /**
   * The shell shared by both blocks. Subclasses implement persist().
   */
  class CartOptin extends HTMLElement {
    connectedCallback() {
      this.checkbox = this.querySelector('.cart-optin__checkbox');
      this.textarea = this.querySelector('.cart-optin__textarea');
      this.counter = this.querySelector('.cart-optin__counter');
      this.disclosure = this.querySelector('.cart-optin__disclosure');
      this.preview = this.querySelector('.cart-optin__preview');
      this.subline = this.querySelector('.cart-optin__subline');

      if (!this.checkbox || !this.textarea) {
        return;
      }

      // Takes the panel off :checked and onto --open; see the note in the stylesheet.
      this.classList.add('cart-optin--js');
      this.open = this.checkbox.checked && this.textarea.value.trim() === '';

      this.debounceTimeout = null;
      this.busy = false;
      this.pendingSave = null;
      this.lastSavedValue = this.textarea.value;
      this.lastSavedChecked = this.checkbox.checked;
      this.flush = this.flush.bind(this);
      this.onPointerDownOutside = this.onPointerDownOutside.bind(this);

      /*
       * connectedCallback runs again whenever the element is re-attached, so every listener is tied to a controller
       * that disconnectedCallback aborts. Binding twice used to register the disclosure handler twice, and the
       * second call simply undid the first — the panel opened and closed again within the same click.
       */
      this.abortController = new AbortController();
      const signal = this.abortController.signal;

      this.checkbox.addEventListener('change', () => this.onToggle(), { signal });
      this.disclosure?.addEventListener('click', () => this.onDisclosureClick(), { signal });
      this.addEventListener('focusout', () => this.onFocusOut(), { signal });
      this.textarea.addEventListener('input', () => this.onInput(), { signal });
      this.textarea.addEventListener('change', this.flush, { signal }); // Fires on blur, incl. clicking checkout
      window.addEventListener('pagehide', this.flush, { signal });

      this.updateCounter();
      this.autosize();
      this.setOpen(this.open, { silent: true });
      this.onConnected();
    }

    disconnectedCallback() {
      this.flush();
      this.abortController?.abort();
      document.removeEventListener('pointerdown', this.onPointerDownOutside);
      this.onDisconnected();
    }

    onConnected() {}
    onDisconnected() {}

    /**
     * Opting out clears the value as well, so an order can never carry something the customer turned off. Written
     * straight away — there is nothing to debounce about a click. Ticking moves focus into the field, which the
     * customer is about to use anyway.
     */
    onToggle() {
      if (!this.checkbox.checked) {
        this.textarea.value = '';
      }

      this.clearDebounce();
      this.save();
      this.setOpen(this.checkbox.checked);

      if (this.checkbox.checked) {
        this.textarea.focus();
      }
    }

    /**
     * The row only shows and hides. Opening it while unticked also ticks the box, since that is what someone
     * opening the block for the first time is asking for; closing it never touches the value.
     */
    onDisclosureClick() {
      if (!this.checkbox.checked) {
        this.checkbox.checked = true;
        this.clearDebounce();
        this.save();
        this.setOpen(true);
        this.textarea.focus();
        return;
      }

      this.setOpen(!this.open);

      if (this.open) {
        this.textarea.focus();
      }
    }

    /**
     * Tapping or tabbing anywhere outside puts the field away, keeping both the value and the tick. Deferred by a
     * frame because focus is momentarily on nothing while it moves between elements.
     */
    /**
     * A tap outside closes the panel too. focusout covers keyboard and most taps, but not every browser moves
     * focus when the customer taps a non-interactive part of the page — on a phone that is most of the screen.
     */
    onPointerDownOutside(event) {
      if (!this.open || this.contains(event.target)) {
        return;
      }

      this.flush();
      this.setOpen(false);
    }

    onFocusOut() {
      window.setTimeout(() => {
        if (!this.isConnected || !this.open) {
          return;
        }

        if (this.contains(document.activeElement)) {
          return;
        }

        this.flush();
        this.setOpen(false);
      }, 0);
    }

    setOpen(open, options = {}) {
      this.open = open;
      this.classList.toggle('cart-optin--open', open);

      document.removeEventListener('pointerdown', this.onPointerDownOutside);
      if (open) {
        document.addEventListener('pointerdown', this.onPointerDownOutside);
      }
      this.disclosure?.setAttribute('aria-expanded', open ? 'true' : 'false');
      this.updatePreview();

      if (open) {
        this.autosize();
      }

      // Only one panel at a time: two open fields leave almost no room for the line items in the drawer.
      if (open && !options.silent) {
        document.querySelectorAll('.cart-optin--open').forEach((el) => {
          if (el !== this && typeof el.setOpen === 'function') {
            el.flush();
            el.setOpen(false);
          }
        });
      }
    }

    /**
     * While the panel is closed a saved message replaces the subline, so putting the field away never reads as
     * losing it.
     */
    updatePreview() {
      if (!this.preview || !this.subline) {
        return;
      }

      const value = this.textarea.value.trim().replace(/\s+/g, ' ');
      const show = !this.open && value !== '';

      this.preview.textContent = value;
      this.preview.hidden = !show;
      this.subline.hidden = show;
    }

    onInput() {
      this.updateCounter();
      this.autosize();
      this.updatePreview();
      this.clearDebounce();
      this.debounceTimeout = window.setTimeout(this.flush, DEBOUNCE_DELAY);
    }

    flush() {
      this.clearDebounce();

      if (this.textarea.value === this.lastSavedValue && this.checkbox.checked === this.lastSavedChecked) {
        return;
      }

      this.save();
    }

    async save(options = {}) {
      /*
       * The guard stops two line mutations overlapping, which would add the gift line twice. Returning outright
       * used to lose the newer value: a save landing mid-flight was dropped and nothing came back for it, so a
       * customer who kept typing while a write was in progress could check out with an earlier draft. The request
       * is remembered instead and replayed once the current one finishes.
       */
      if (this.busy) {
        this.pendingSave = options;
        return;
      }

      const checked = this.checkbox.checked;
      const value = checked ? this.textarea.value : '';

      this.lastSavedValue = value;
      this.lastSavedChecked = checked;
      this.render(checked);

      this.busy = true;

      try {
        await this.persist(checked, value, options);
      } finally {
        this.busy = false;
      }

      const pending = this.pendingSave;
      this.pendingSave = null;

      // Only replay if the field actually moved on; otherwise this would recurse on every save.
      if (pending && (this.textarea.value !== this.lastSavedValue || this.checkbox.checked !== this.lastSavedChecked)) {
        await this.save(pending);
      }
    }

    // eslint-disable-next-line no-unused-vars
    async persist(checked, value, options) {}

    render(checked) {
      this.classList.toggle('cart-optin--active', checked);
      this.updateCounter();
      this.autosize();
      this.updatePreview();
    }

    /**
     * Grows the field to fit what is in it, so an empty card is one line tall rather than reserving room for the
     * full 200 characters. The CSS min-height sets the floor and max-height the ceiling; past the ceiling the
     * field scrolls.
     */
    autosize() {
      if (SUPPORTS_FIELD_SIZING || !this.textarea) {
        return;
      }

      this.textarea.style.height = 'auto';
      this.textarea.style.height = `${this.textarea.scrollHeight}px`;
    }

    clearDebounce() {
      if (this.debounceTimeout) {
        window.clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }
    }

    updateCounter() {
      const max = parseInt(this.getAttribute('data-max-characters'), 10);

      if (!this.counter || !max) {
        return;
      }

      this.counter.textContent = `${this.textarea.value.length}/${max}`;
    }
  }

  /**
   * "Bestellhinweis hinzufügen" — writes cart.note and nothing else.
   */
  class CartOrderNote extends CartOptin {
    async persist(checked, value) {
      await writeCart({ note: value });
    }
  }

  /**
   * "Als Geschenk versenden" — adds or removes the gift line and writes the message onto it as a line item
   * property, which is what makes it visible in the checkout order summary. The cart attributes mirror it.
   */
  class CartGiftNote extends CartOptin {
    onConnected() {
      this.flagAttribute = this.dataset.flagAttribute;
      this.flagValue = this.dataset.flagValue;
      this.noteKey = this.dataset.noteKey;
      this.variantId = this.dataset.giftVariantId ? Number(this.dataset.giftVariantId) : null;

      this.onSiblingChange = this.onSiblingChange.bind(this);
      document.addEventListener(SYNC_EVENT, this.onSiblingChange, { signal: this.abortController.signal });

      this.reconcile();
    }

    /**
     * Brings the cart in line with what was rendered. Two cases:
     *
     *   - a cart from the previous theme, carrying the old attributes and possibly a bare gift line: the message is
     *     moved onto the line as a property, the current attributes are written, the old ones deleted;
     *   - a cart whose gift line was removed from the line item list: the stale attributes are cleared.
     *
     * Only one instance does the work — on the cart page both are connected, and two identical writes would race.
     */
    reconcile() {
      if (this.dataset.reconciled === 'true') {
        return;
      }

      const migrating = this.dataset.migrate === 'true';
      const stale = this.dataset.stale === 'true';

      if (!migrating && !stale) {
        return;
      }

      document.querySelectorAll('cart-gift-note').forEach((el) => {
        el.dataset.reconciled = 'true';
        delete el.dataset.migrate;
        delete el.dataset.stale;
      });

      this.save({ force: true });
    }

    async persist(checked, value, options = {}) {
      const attributes = { [this.flagAttribute]: checked ? this.flagValue : '', [this.noteKey]: value };

      if (options.force === true) {
        LEGACY_ATTRIBUTES.forEach((key) => {
          attributes[key] = '';
        });
      }

      document.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { isGift: checked, note: value, source: this } }));

      /*
       * Strictly sequential, and the line item property goes first.
       *
       * /cart/update.js and /cart/change.js both rewrite the whole cart document, so running them concurrently
       * makes them lose each other's update — sending them together left the property correct and the attribute a
       * revision behind. They cannot be combined either: update.js takes attributes but not line properties, and
       * change.js the reverse.
       *
       * The property is written first because it is the one the customer sees in the checkout summary. If a
       * navigation cuts the pair short, the mirror lags rather than the value that is actually on display.
       */
      if (this.variantId) {
        await this.syncGiftLine(checked, value);
      }

      await writeCart({ attributes });
    }

    async syncGiftLine(isGift, note) {
      const lineKey = this.dataset.giftLineKey;

      if (isGift && !lineKey) {
        const cart = await mutateCartLine('add', {
          items: [{ id: this.variantId, quantity: 1, properties: { [this.noteKey]: note } }]
        });

        if (cart) {
          this.adoptGiftLineKey(cart);
        }

        return;
      }

      if (!isGift && lineKey) {
        await mutateCartLine('change', { id: lineKey, quantity: 0 });
        return;
      }

      // Ticked with the line already in the cart. This is the path a migrated cart takes — the previous theme
      // added the line without any properties, so the message has to be written onto it now.
      if (isGift && lineKey) {
        await this.updateGiftLineProperties(note);
      }
    }

    /**
     * Rewrites the message on an existing line. Quantities and totals are untouched, so this deliberately requests
     * no sections and dispatches no event: a re-render here would destroy the focused textarea.
     *
     * A line item key is variantId:hash(properties), so **writing a property changes the key**. The key seeded from
     * Liquid is only good for the first write; after that the line answers to a new one and the old key returns
     * 400 "no valid id or line parameters". The new key is adopted from the response, and a stale one — which a
     * sibling instance's write can also cause — is re-resolved and retried once.
     */
    async updateGiftLineProperties(note, isRetry = false) {
      const lineKey = this.dataset.giftLineKey;

      if (!lineKey) {
        return;
      }

      const response = await fetch(cartUrl('change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, properties: { [this.noteKey]: note } }),
        keepalive: true
      });

      if (response.ok) {
        this.adoptGiftLineKey(await response.json());
        return;
      }

      if (isRetry) {
        return;
      }

      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      this.adoptGiftLineKey(cart);

      if (this.dataset.giftLineKey && this.dataset.giftLineKey !== lineKey) {
        await this.updateGiftLineProperties(note, true);
      }
    }

    /**
     * Points the element at whatever key the gift line currently answers to.
     */
    adoptGiftLineKey(cart) {
      const line = cart?.items?.find((item) => item.variant_id === this.variantId);

      if (line) {
        this.dataset.giftLineKey = line.key;
      } else {
        delete this.dataset.giftLineKey;
      }
    }

    /**
     * Mirrors what the other instance on the page just saved, without saving it a second time.
     */
    onSiblingChange(event) {
      if (event.detail.source === this) {
        return;
      }

      this.checkbox.checked = event.detail.isGift;
      this.lastSavedChecked = event.detail.isGift;

      // Never overwrite a field the customer is typing in, and leave its pending save on the clock
      if (document.activeElement !== this.textarea) {
        this.clearDebounce();
        this.textarea.value = event.detail.note;
        this.lastSavedValue = event.detail.note;
        this.autosize();
      }

      this.render(event.detail.isGift);
      this.setOpen(event.detail.isGift && this.open, { silent: true });
    }
  }

  /**
   * Rendered by snippets/cart-optin-reset.liquid in the empty-cart branch, and only when something is left to
   * clear. Shopify keeps cart.note and the cart attributes when the last line item is removed, and the gift line
   * itself survives because it is hidden rather than absent — so an emptied cart would otherwise hand a stale gift
   * message and order note to whatever the customer buys next.
   *
   * It clears once and is gone: after the reset the snippet's condition is false, so the element is not rendered
   * again and there is no loop.
   */
  class CartOptinReset extends HTMLElement {
    async connectedCallback() {
      if (this.dataset.done === 'true') {
        return;
      }

      this.dataset.done = 'true';

      // The only line that can still be here is the hidden gift line; clearing is what removes it.
      if (this.dataset.clearLineItems === 'true') {
        await fetch(cartUrl('clear'), { method: 'POST', keepalive: true });
      }

      await writeCart({
        note: '',
        attributes: {
          [this.dataset.flagAttribute]: '',
          [this.dataset.noteKey]: '',
          ...Object.fromEntries(LEGACY_ATTRIBUTES.map((key) => [key, '']))
        }
      });

      // Everything else that shows a cart total or count is now out of date.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }
  }

  if (!window.customElements.get('cart-optin-reset')) {
    window.customElements.define('cart-optin-reset', CartOptinReset);
  }

  window.customElements.define('cart-gift-note', CartGiftNote);

  if (!window.customElements.get('cart-order-note')) {
    window.customElements.define('cart-order-note', CartOrderNote);
  }
})();
